import type {
  AcaoOtimizacaoResposta,
  ComparacaoOperacoes,
  ContaAnuncioResumo,
  Diagnostico,
  EntradaAtualizarContaAnuncio,
  EntradaCriarContaAnuncio,
  EntradaDecidirVariacao,
  EntradaExecutarAcao,
  EntradaGerarVariacao,
  EntradaPerguntarOtimizador,
  EnvelopeAgregado,
  ListagemHierarquia,
  PainelMetaAds,
  RecomendacaoResposta,
  RespostaOtimizador,
  VariacaoCriativoResposta,
} from '@baiocchi/shared'

import { api, type ParametrosConsulta } from './cliente'

/**
 * `/meta-ads` — Command Center e Otimizador.
 *
 * Toda resposta de leitura vem no envelope `{ dados, meta }`, e aqui
 * `meta.defasagemMinutos` **não é zero**: os números vêm de sincronização periódica com a
 * Meta, não da tabela base. A tela é obrigada a mostrar esse carimbo — sem ele, o gestor
 * compara com o Gerenciador de Anúncios, vê diferença e conclui que o sistema está errado.
 *
 * Nenhuma credencial da Meta passa por aqui (§23 da missão): o navegador nunca fala com
 * `graph.facebook.com`, e o `act_id` chega mascarado do servidor.
 */
export const metaAdsApi = {
  contas(sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<ContaAnuncioResumo> }>('/meta-ads/contas', undefined, sinal)
  },

  painel(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeAgregado<PainelMetaAds>>('/meta-ads/painel', filtros, sinal)
  },

  /**
   * Hierarquia. Sem `campanhaId`/`conjuntoId` devolve campanhas; com eles, o nível
   * abaixo. Um método para os três níveis porque o backend é um endpoint para os três.
   */
  hierarquia(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeAgregado<ListagemHierarquia>>('/meta-ads/hierarquia', filtros, sinal)
  },

  diagnostico(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeAgregado<Diagnostico>>('/meta-ads/diagnostico', filtros, sinal)
  },

  comparacaoOperacoes(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<EnvelopeAgregado<ComparacaoOperacoes>>(
      '/meta-ads/comparacao-operacoes',
      filtros,
      sinal,
    )
  },

  recomendacoes(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<RecomendacaoResposta> }>(
      '/meta-ads/recomendacoes',
      filtros,
      sinal,
    )
  },

  narrarRecomendacao(id: string) {
    return api.post<{ narrativa: string | null }>(`/meta-ads/recomendacoes/${id}/narrar`, {})
  },

  descartarRecomendacao(id: string, motivo?: string) {
    return api.post<{ descartada: true }>(`/meta-ads/recomendacoes/${id}/descartar`, { motivo })
  },

  otimizacoes(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<AcaoOtimizacaoResposta> }>(
      '/meta-ads/otimizacoes',
      filtros,
      sinal,
    )
  },

  perguntar(corpo: EntradaPerguntarOtimizador) {
    return api.post<RespostaOtimizador>('/meta-ads/otimizador/perguntar', corpo)
  },

  variacoes(filtros?: ParametrosConsulta, sinal?: AbortSignal) {
    return api.get<{ dados: ReadonlyArray<VariacaoCriativoResposta> }>(
      '/meta-ads/variacoes',
      filtros,
      sinal,
    )
  },

  gerarVariacao(corpo: EntradaGerarVariacao) {
    return api.post<VariacaoCriativoResposta>('/meta-ads/variacoes', corpo)
  },

  decidirVariacao(id: string, corpo: EntradaDecidirVariacao) {
    return api.post<VariacaoCriativoResposta>(`/meta-ads/variacoes/${id}/decidir`, corpo)
  },

  criarConta(corpo: EntradaCriarContaAnuncio) {
    return api.post<ContaAnuncioResumo>('/meta-ads/contas', corpo)
  },

  atualizarConta(id: number, corpo: EntradaAtualizarContaAnuncio) {
    return api.patch<ContaAnuncioResumo>(`/meta-ads/contas/${id}`, corpo)
  },

  sincronizar(filtros?: ParametrosConsulta) {
    return api.post<{ estrutura: unknown; insights: unknown }>(
      '/meta-ads/sincronizar',
      {},
      filtros,
    )
  },

  /**
   * Executa uma ação na Meta.
   *
   * `confirmado: true` é exigido pelo schema do servidor e a tela **precisa** enviá-lo
   * explicitamente — não é um valor que este cliente preenche por conta própria. É a
   * terceira das quatro barreiras: o booleano existe para que nenhuma ação na conta de
   * anúncio possa sair de um clique acidental ou de um retry de rede.
   */
  executarAcao(corpo: EntradaExecutarAcao) {
    return api.post<AcaoOtimizacaoResposta>('/meta-ads/acoes', corpo)
  },
}
